import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Facebook, Instagram, Linkedin, Twitter, Loader2 } from 'lucide-react';
import { EmptyState } from './shared';

export const FooterSection = ({ siteContent, setSiteContent, onSave, saving }) => {
  const [activeTab, setActiveTab] = useState('social');
  
  if (!siteContent) return <EmptyState message="Kunde inte ladda innehåll" />;

  const footer = siteContent.footer || {};

  const tabs = [
    { id: 'social', label: 'Sociala medier' },
    { id: 'contact', label: 'Kontaktinfo' },
    { id: 'about', label: 'Om oss' },
    { id: 'terms', label: 'Köpvillkor' },
    { id: 'privacy', label: 'Integritetspolicy' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-footer">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          <strong>Tips:</strong> Ändringarna du gör här visas på sidorna Om oss, Köpvillkor, Integritetspolicy och FAQ. 
          Du kan använda ** för fetstil och ## för rubriker.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#003B73] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'social' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Sociala medier</h3>
          <p className="text-sm text-gray-500 mb-4">Länkarna visas i footern. Lämna tomt för att dölja.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2"><Facebook className="h-4 w-4" /> Facebook</Label>
              <Input value={footer.facebook_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, facebook_url: e.target.value } })} placeholder="https://facebook.com/företagsnamn" className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram</Label>
              <Input value={footer.instagram_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, instagram_url: e.target.value } })} placeholder="https://instagram.com/användarnamn" className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-2"><Linkedin className="h-4 w-4" /> LinkedIn</Label>
              <Input value={footer.linkedin_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, linkedin_url: e.target.value } })} placeholder="https://linkedin.com/company/företag" className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-2"><Twitter className="h-4 w-4" /> X (Twitter)</Label>
              <Input value={footer.twitter_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, twitter_url: e.target.value } })} placeholder="https://x.com/användarnamn" className="mt-1" />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contact' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Kontaktinformation</h3>
          <p className="text-sm text-gray-500 mb-4">Visas i footern och på kontaktsidan.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>E-postadress</Label>
              <Input value={footer.contact_email || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, contact_email: e.target.value } })} placeholder="info@företag.se" className="mt-1" />
            </div>
            <div>
              <Label>Telefonnummer</Label>
              <Input value={footer.contact_phone || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, contact_phone: e.target.value } })} placeholder="08-123 456 78" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Adress</Label>
              <Textarea value={footer.contact_address || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, contact_address: e.target.value } })} placeholder="Gatunamn 1&#10;123 45 Stockholm" className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>Kort beskrivning (visas i footern)</Label>
              <Textarea value={footer.about_text || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, about_text: e.target.value } })} placeholder="En kort beskrivning av företaget..." className="mt-1" rows={2} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'about' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Om oss-sidan</h3>
          <p className="text-sm text-gray-500 mb-4">Skriv texten som visas på /om-oss. Använd ** för fetstil och ## för rubriker.</p>
          <Textarea value={footer.about_page || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, about_page: e.target.value } })} placeholder="## Vår historia&#10;&#10;Skriv om ert företag här..." className="mt-1 font-mono text-sm" rows={15} />
          <p className="text-xs text-gray-400 mt-2">Formatering: **fetstil**, ## Rubrik, ### Underrubrik, - punkt</p>
        </div>
      )}

      {activeTab === 'terms' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Köpvillkor & Ångerrätt</h3>
          <p className="text-sm text-gray-500 mb-4">Skriv era köpvillkor som visas på /kopvillkor.</p>
          <Textarea value={footer.terms_page || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, terms_page: e.target.value } })} placeholder="## Köpvillkor&#10;&#10;**1. Allmänt**&#10;Dessa köpvillkor gäller för..." className="mt-1 font-mono text-sm" rows={15} />
        </div>
      )}

      {activeTab === 'privacy' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Integritetspolicy</h3>
          <p className="text-sm text-gray-500 mb-4">Skriv er integritetspolicy som visas på /integritetspolicy.</p>
          <Textarea value={footer.privacy_page || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, privacy_page: e.target.value } })} placeholder="## Integritetspolicy&#10;&#10;Vi värnar om din personliga integritet..." className="mt-1 font-mono text-sm" rows={15} />
        </div>
      )}

      {activeTab === 'faq' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-2">Vanliga frågor (FAQ)</h3>
          <p className="text-sm text-gray-500 mb-4">Skriv vanliga frågor och svar som visas på /faq.</p>
          <Textarea value={footer.faq_page || ''} onChange={(e) => setSiteContent({ ...siteContent, footer: { ...footer, faq_page: e.target.value } })} placeholder="**Hur beställer jag?**&#10;Sök efter produkter, lägg i varukorgen..." className="mt-1 font-mono text-sm" rows={15} />
          <p className="text-xs text-gray-400 mt-2">Tips: Skriv frågan med **fetstil** och svaret på raden under.</p>
        </div>
      )}

      <Button className="bg-[#003B73]" onClick={onSave} disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Spara ändringar
      </Button>
    </div>
  );
};
